import type { MessageTypes, SignTypedDataVersion, TypedDataV1, TypedMessage } from '@metamask/eth-sig-util';
import { Buffer } from 'buffer';
import { Auth } from '../auth';
import { rpcUrl } from '../constant';
import { AuthError, EIP1559TxData, PrefixedHexString, TxData } from '../types';
import { addHexPrefix } from '../utils/hex-utils';

class EvmService {
    constructor(private auth: Auth) {}

    private assertEVM() {
        if (this.auth.getChain()?.name?.toLowerCase() === 'solana') {
            throw AuthError.unsupportedMethod();
        }
    }

    public async sendTransaction(transaction: TxData | EIP1559TxData): Promise<PrefixedHexString> {
        this.assertEVM();
        return this.auth.sendTransaction(addHexPrefix(Buffer.from(JSON.stringify(transaction)).toString('hex')));
    }

    public async personalSign(message: PrefixedHexString): Promise<PrefixedHexString> {
        this.assertEVM();
        return this.auth.sign('personal_sign', message);
    }

    public async personalSignUniq(message: PrefixedHexString): Promise<PrefixedHexString> {
        this.assertEVM();
        return this.auth.sign('personal_sign_uniq', message);
    }

    public async signTypedData<V extends SignTypedDataVersion, T extends MessageTypes>({
        data,
        version,
    }: {
        data: V extends 'V1' ? TypedDataV1 : TypedMessage<T>;
        version: V;
    }): Promise<PrefixedHexString> {
        this.assertEVM();
        return this.auth.sign(
            `eth_signTypedData_${version.toLowerCase()}`,
            addHexPrefix(Buffer.from(JSON.stringify(data)).toString('hex'))
        );
    }

    public async signTypedDataUniq<T extends MessageTypes>(data: TypedMessage<T>): Promise<PrefixedHexString> {
        this.assertEVM();
        return this.auth.sign(
            'eth_signTypedData_v4_uniq',
            addHexPrefix(Buffer.from(JSON.stringify(data)).toString('hex'))
        );
    }

    public async getAddress(): Promise<PrefixedHexString | undefined> {
        return this.auth.getEVMAddress();
    }

    public getRpcUrl(): string {
        this.assertEVM();
        const domain = `${rpcUrl()}/evm-chain`;
        return `${domain}?chainId=${this.auth.getChain().id}&projectUuid=${this.auth.config.projectId}&projectKey=${
            this.auth.config.clientKey
        }`;
    }
}

export default EvmService;
